import { formatCellValue } from 'components/data-table/utils';
import type { DataTableColumnMeta } from 'components/data-table';
import type { XlsxExportColumn } from 'utils/tables';
import type { PerforationRow } from 'types/well-perforations';
import { perforationsColumns } from './perforations-columns';
import type { PerforationGroupRow } from './perforations-grouping';

/**
 * Export columns for the perforation-interval rows, **derived** from
 * `perforationsColumns` rather than hand-listed beside it.
 *
 * The outer table shows one summary row per completion, so DataTable's built-in
 * export — which walks the outer table's own rows and columns — would emit only
 * the five group columns and drop every interval. This list is what makes the
 * nested detail exportable, and deriving it means a column added to the
 * interval table can't silently go missing from the file.
 *
 * `key` is the `accessorKey` (the field the API actually emits), not the column
 * `id` — several ids diverge from their accessor (`topDepth` →
 * `perfTopDepthFt`), and the export util indexes the row object by key.
 *
 * Values stay raw so Excel keeps numbers numeric and sortable; only dates get a
 * formatter, matching how the on-screen cell renders. This mirrors
 * `LANDING_ANALYSIS_EXPORT_COLUMNS`, which likewise formats only what needs it.
 */
export const perforationIntervalExportColumns: XlsxExportColumn[] =
  perforationsColumns.map((col) => {
    const key = String(
      (col as { accessorKey?: unknown }).accessorKey ?? col.id ?? ''
    );
    const header =
      typeof col.header === 'string' ? col.header : String(col.id ?? key);
    const meta = col.meta as DataTableColumnMeta | undefined;
    const isDate = meta?.type === 'date' || meta?.type === 'datetime';

    return {
      key,
      header,
      ...(isDate
        ? {
            // Blank rather than the table's em-dash placeholder — an empty cell
            // is what a spreadsheet consumer expects for "no value".
            formatter: (value: unknown) => {
              if (value === null || value === undefined) return '';
              return formatCellValue(value, meta);
            },
          }
        : {}),
    };
  });

/**
 * Every perforation interval across every completion, in the order the table
 * lays them out: completions shallowest-first, and each completion's intervals
 * shallowest-first within it.
 *
 * Expansion state is irrelevant — `groups` already carries each completion's
 * full interval list, so a collapsed row exports exactly like an expanded one.
 *
 * @param {PerforationGroupRow[]} groups - Completion groups from
 *   `groupPerforationsByCompletion`, already sorted.
 * @return {PerforationRow[]} The flat interval rows to write to the file.
 */
export function flattenPerforationIntervals(
  groups: PerforationGroupRow[]
): PerforationRow[] {
  return groups.flatMap((group) => group.intervals);
}
